import * as React from 'react';
import { Link } from 'react-router-dom';
import GoogleBusinessService, {
  GoogleBusinessReview,
} from '../services/GoogleBusinessService';
import { useGoogleBusinessConnectedLocations } from '../hooks/useGoogleBusiness';

const MAX_REPLY_BYTES = 4096;

const RATING_MAP: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

function getByteLength(str: string): number {
  return new TextEncoder().encode(str).length;
}

function stripReviewPrefix(reviewId: string): string {
  return reviewId.replace(/^reviews\//, '');
}

function isDisconnectedError(err: any): boolean {
  const data = err?.response?.data ?? err?.data;
  return data?.disconnected === true;
}

function StarRating({ rating }: { rating: string }): React.JSX.Element {
  const stars = RATING_MAP[rating] || 0;

  return (
    <span style={{ color: '#ffc107', fontSize: 16 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i}>{i <= stars ? '\u2605' : '\u2606'}</span>
      ))}
    </span>
  );
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

interface ReviewCardProps {
  review: GoogleBusinessReview;
  isReplying: boolean;
  isDeleting: boolean;
  replyText: string;
  replyByteLength: number;
  replyBytesRemaining: number;
  submittingReply: boolean;
  editingReplyReviewId: string | null;
  onStartReply: (reviewId: string, existingComment?: string) => void;
  onCancelReply: () => void;
  onSubmitReply: (reviewId: string) => void;
  onDeleteReply: (reviewId: string) => void;
  onReplyTextChange: (text: string) => void;
}

const ReviewCard = React.memo(function ReviewCard({
  review,
  isReplying,
  isDeleting,
  replyText,
  replyByteLength,
  replyBytesRemaining,
  submittingReply,
  editingReplyReviewId,
  onStartReply,
  onCancelReply,
  onSubmitReply,
  onDeleteReply,
  onReplyTextChange,
}: ReviewCardProps): React.JSX.Element {
  return (
    <div
      style={{
        padding: 16,
        marginBottom: 16,
        background: '#fafafa',
        borderRadius: 6,
        border: '1px solid #eee',
      }}
    >
      {/* Reviewer Info */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        {review.reviewer.profilePhotoUrl ? (
          <img
            src={review.reviewer.profilePhotoUrl}
            alt=""
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              marginRight: 12,
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              marginRight: 12,
              background: '#e0e3ef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              fontWeight: 600,
              color: '#666',
            }}
          >
            {review.reviewer.displayName?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#0f2b5a' }}>
            {review.reviewer.displayName || 'Anonym'}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {formatDate(review.createTime)}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <StarRating rating={review.starRating} />
        </div>
      </div>

      {/* Review Comment */}
      {review.comment && (
        <div style={{ fontSize: 14, color: '#333', marginBottom: 12, lineHeight: 1.5 }}>
          {review.comment}
        </div>
      )}

      {/* Existing Reply */}
      {review.reviewReply && !isReplying && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            background: '#e8f5e9',
            borderRadius: 6,
            borderLeft: '3px solid #4caf50',
          }}
        >
          <div style={{ fontSize: 12, color: '#2e7d32', fontWeight: 600, marginBottom: 4 }}>
            Ihre Antwort ({formatDate(review.reviewReply.updateTime)})
          </div>
          <div style={{ fontSize: 14, color: '#333', lineHeight: 1.5 }}>
            {review.reviewReply.comment}
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => onStartReply(review.reviewId, review.reviewReply?.comment)}
              style={{
                padding: '4px 10px',
                background: 'transparent',
                color: '#4285F4',
                border: '1px solid #4285F4',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={() => onDeleteReply(review.reviewId)}
              disabled={isDeleting}
              style={{
                padding: '4px 10px',
                background: 'transparent',
                color: '#ef5350',
                border: '1px solid #ef5350',
                borderRadius: 4,
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                fontSize: 12,
                opacity: isDeleting ? 0.6 : 1,
              }}
            >
              {isDeleting ? 'Löschen...' : 'Löschen'}
            </button>
          </div>
        </div>
      )}

      {/* Reply Form */}
      {isReplying ? (
        <div style={{ marginTop: 12 }}>
          <textarea
            value={replyText}
            onChange={(e) => onReplyTextChange(e.target.value)}
            placeholder="Ihre Antwort..."
            data-testid={`reply-textarea-${review.reviewId}`}
            style={{
              width: '100%',
              minHeight: 80,
              padding: 10,
              fontSize: 14,
              borderRadius: 6,
              border: replyBytesRemaining < 0 ? '1px solid #ef5350' : '1px solid #ccc',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: replyBytesRemaining < 0 ? '#ef5350' : replyBytesRemaining < 200 ? '#ff9800' : '#666',
              }}
            >
              {replyBytesRemaining} Bytes verbleibend
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onCancelReply}
                disabled={submittingReply}
                style={{
                  padding: '6px 12px',
                  background: '#f5f5f5',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  cursor: submittingReply ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => onSubmitReply(review.reviewId)}
                disabled={submittingReply || !replyText.trim() || replyBytesRemaining < 0}
                style={{
                  padding: '6px 12px',
                  background:
                    submittingReply || !replyText.trim() || replyBytesRemaining < 0
                      ? '#ccc'
                      : '#4285F4',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor:
                    submittingReply || !replyText.trim() || replyBytesRemaining < 0
                      ? 'not-allowed'
                      : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {submittingReply
                  ? 'Senden...'
                  : editingReplyReviewId
                  ? 'Aktualisieren'
                  : 'Antworten'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        !review.reviewReply && (
          <button
            type="button"
            onClick={() => onStartReply(review.reviewId)}
            style={{
              marginTop: 12,
              padding: '6px 12px',
              background: 'transparent',
              color: '#4285F4',
              border: '1px solid #4285F4',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Antworten
          </button>
        )
      )}
    </div>
  );
});

export function GoogleBusinessReviews(): React.JSX.Element {
  const { locations: connectedLocations, isLoading: loadingLocations, error: locationsError } = useGoogleBusinessConnectedLocations();
  const [selectedLocationId, setSelectedLocationId] = React.useState<number | null>(null);
  const [reviews, setReviews] = React.useState<GoogleBusinessReview[]>([]);
  const [averageRating, setAverageRating] = React.useState<number | null>(null);
  const [totalReviewCount, setTotalReviewCount] = React.useState<number | null>(null);
  const [nextPageToken, setNextPageToken] = React.useState<string | null>(null);

  const [loadingReviews, setLoadingReviews] = React.useState<boolean>(false);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);

  const [replyingToReviewId, setReplyingToReviewId] = React.useState<string | null>(null);
  const [editingReplyReviewId, setEditingReplyReviewId] = React.useState<string | null>(null);
  const [replyText, setReplyText] = React.useState<string>('');
  const [submittingReply, setSubmittingReply] = React.useState<boolean>(false);
  const [deletingReplyReviewId, setDeletingReplyReviewId] = React.useState<string | null>(null);

  const [error, setError] = React.useState<string>('');
  const [success, setSuccess] = React.useState<string>('');

  const replyByteLength = React.useMemo(() => getByteLength(replyText), [replyText]);
  const replyBytesRemaining = MAX_REPLY_BYTES - replyByteLength;

  React.useEffect(() => {
    if (locationsError) {
      const statusCode = locationsError?.response?.status;
      if (statusCode === 400) {
        setError('Google Business Profile ist nicht verbunden. Bitte verbinden Sie sich zuerst in den Einstellungen.');
      } else {
        const detail = locationsError?.response?.data?.detail || locationsError?.message || 'Fehler beim Laden der Standorte';
        setError(detail);
      }
    }
  }, [locationsError]);

  React.useEffect(() => {
    if (connectedLocations.length === 1 && !selectedLocationId) {
      setSelectedLocationId(connectedLocations[0].id);
    }
  }, [connectedLocations, selectedLocationId]);

  const loadReviews = React.useCallback(async (locationId: number, pageToken?: string) => {
    if (pageToken) {
      setLoadingMore(true);
    } else {
      setLoadingReviews(true);
      setReviews([]);
      setAverageRating(null);
      setTotalReviewCount(null);
    }
    setError('');

    try {
      const result = await GoogleBusinessService.getReviews(locationId, pageToken);

      if (pageToken) {
        setReviews((prev) => [...prev, ...result.reviews]);
      } else {
        setReviews(result.reviews);
        setAverageRating(result.averageRating ?? null);
        setTotalReviewCount(result.totalReviewCount ?? null);
      }
      setNextPageToken(result.nextPageToken ?? null);
    } catch (e: any) {
      if (isDisconnectedError(e)) {
        setError('Die Google-Verbindung wurde widerrufen. Bitte verbinden Sie sich erneut.');
      } else {
        const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Laden der Bewertungen';
        setError(detail);
      }
    } finally {
      setLoadingReviews(false);
      setLoadingMore(false);
    }
  }, []);

  React.useEffect(() => {
    if (selectedLocationId) {
      loadReviews(selectedLocationId);
    }
  }, [selectedLocationId, loadReviews]);

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedLocationId(value ? parseInt(value, 10) : null);
    setReplyingToReviewId(null);
    setEditingReplyReviewId(null);
    setReplyText('');
  };

  const handleLoadMore = () => {
    if (selectedLocationId && nextPageToken) {
      loadReviews(selectedLocationId, nextPageToken);
    }
  };

  const handleStartReply = React.useCallback((reviewId: string, existingComment?: string) => {
    setReplyingToReviewId(reviewId);
    setEditingReplyReviewId(existingComment ? reviewId : null);
    setReplyText(existingComment || '');
    setError('');
    setSuccess('');
  }, []);

  const handleCancelReply = React.useCallback(() => {
    setReplyingToReviewId(null);
    setEditingReplyReviewId(null);
    setReplyText('');
  }, []);

  const handleSubmitReply = React.useCallback(async (reviewId: string) => {
    if (!selectedLocationId || !replyText.trim()) return;

    if (replyByteLength > MAX_REPLY_BYTES) {
      setError(`Antwort ist zu lang (${replyByteLength} Bytes). Maximum: ${MAX_REPLY_BYTES} Bytes.`);
      return;
    }

    setSubmittingReply(true);
    setError('');
    setSuccess('');

    try {
      const replyResult = await GoogleBusinessService.replyToReview(
        selectedLocationId,
        stripReviewPrefix(reviewId),
        replyText.trim()
      );

      setReviews((prev) =>
        prev.map((r) =>
          r.reviewId === reviewId
            ? { ...r, reviewReply: replyResult }
            : r
        )
      );

      setSuccess(editingReplyReviewId ? 'Antwort aktualisiert' : 'Antwort gesendet');
      handleCancelReply();
    } catch (e: any) {
      if (isDisconnectedError(e)) {
        setError('Die Google-Verbindung wurde widerrufen. Bitte verbinden Sie sich erneut.');
        return;
      }
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Senden der Antwort';
      setError(detail);
    } finally {
      setSubmittingReply(false);
    }
  }, [selectedLocationId, replyText, replyByteLength, editingReplyReviewId, handleCancelReply]);

  const handleDeleteReply = React.useCallback(async (reviewId: string) => {
    if (!selectedLocationId) return;
    if (!window.confirm('Möchten Sie diese Antwort wirklich löschen?')) return;

    setDeletingReplyReviewId(reviewId);
    setError('');
    setSuccess('');

    try {
      await GoogleBusinessService.deleteReply(selectedLocationId, stripReviewPrefix(reviewId));

      setReviews((prev) =>
        prev.map((r) =>
          r.reviewId === reviewId
            ? { ...r, reviewReply: undefined }
            : r
        )
      );

      setSuccess('Antwort gelöscht');
    } catch (e: any) {
      if (isDisconnectedError(e)) {
        setError('Die Google-Verbindung wurde widerrufen. Bitte verbinden Sie sich erneut.');
        return;
      }
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Löschen der Antwort';
      setError(detail);
    } finally {
      setDeletingReplyReviewId(null);
    }
  }, [selectedLocationId]);

  const handleReplyTextChange = React.useCallback((text: string) => {
    setReplyText(text);
  }, []);

  const selectedLocation = connectedLocations.find((loc) => loc.id === selectedLocationId);

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 24, color: '#0f2b5a', margin: 0 }}>Google Bewertungen</h1>
        <Link
          to="/settings/integrations/google-business"
          style={{
            fontSize: 13,
            color: '#4285F4',
            textDecoration: 'none',
          }}
        >
          Einstellungen
        </Link>
      </div>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>
        Verwalten Sie die Google Bewertungen Ihrer verbundenen Standorte.
      </p>

      {error && (
        <div
          data-testid="google-reviews-error"
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            color: '#c62828',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          data-testid="google-reviews-success"
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#e8f5e9',
            border: '1px solid #66bb6a',
            borderRadius: 6,
            color: '#2e7d32',
            fontSize: 14,
          }}
        >
          {success}
        </div>
      )}

      {/* Location Selector */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: 8,
          padding: 20,
          marginBottom: 16,
        }}
      >
        <label
          htmlFor="location-select"
          style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#0f2b5a', marginBottom: 8 }}
        >
          Standort auswählen
        </label>
        {loadingLocations ? (
          <span style={{ fontSize: 14, color: '#666' }}>Lade Standorte...</span>
        ) : connectedLocations.length === 0 ? (
          <div style={{ fontSize: 14, color: '#666' }}>
            Keine verbundenen Standorte gefunden.{' '}
            <Link to="/settings/integrations/google-business" style={{ color: '#4285F4' }}>
              Standorte verbinden
            </Link>
          </div>
        ) : (
          <select
            id="location-select"
            data-testid="google-reviews-location-select"
            value={selectedLocationId ?? ''}
            onChange={handleLocationChange}
            style={{
              width: '100%',
              maxWidth: 400,
              padding: '10px 12px',
              fontSize: 14,
              borderRadius: 6,
              border: '1px solid #ccc',
            }}
          >
            <option value="">Standort auswählen...</option>
            {connectedLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.location_name}
                {loc.address ? ` - ${loc.address}` : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Stats Bar */}
      {selectedLocationId && !loadingReviews && reviews.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 24,
            padding: 16,
            marginBottom: 16,
            background: '#f8f9fa',
            border: '1px solid #e0e3ef',
            borderRadius: 6,
          }}
        >
          {averageRating !== null && (
            <div>
              <span style={{ fontSize: 13, color: '#666' }}>Durchschnitt:</span>
              <span style={{ marginLeft: 8, fontSize: 16, fontWeight: 600, color: '#0f2b5a' }}>
                {averageRating.toFixed(1)}
              </span>
              <StarRating rating={averageRating >= 4.5 ? 'FIVE' : averageRating >= 3.5 ? 'FOUR' : averageRating >= 2.5 ? 'THREE' : averageRating >= 1.5 ? 'TWO' : 'ONE'} />
            </div>
          )}
          {totalReviewCount !== null && (
            <div>
              <span style={{ fontSize: 13, color: '#666' }}>Bewertungen gesamt:</span>
              <span style={{ marginLeft: 8, fontSize: 16, fontWeight: 600, color: '#0f2b5a' }}>
                {totalReviewCount}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Reviews List */}
      {selectedLocationId && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            padding: 20,
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: '0 0 16px 0' }}>
            Bewertungen
            {selectedLocation && ` - ${selectedLocation.location_name}`}
          </h2>

          {loadingReviews ? (
            <p style={{ fontSize: 14, color: '#666' }}>Lade Bewertungen...</p>
          ) : reviews.length === 0 ? (
            <p style={{ fontSize: 14, color: '#666' }}>Keine Bewertungen gefunden.</p>
          ) : (
            <div data-testid="google-reviews-list">
              {reviews.map((review) => (
                <ReviewCard
                  key={review.reviewId}
                  review={review}
                  isReplying={replyingToReviewId === review.reviewId}
                  isDeleting={deletingReplyReviewId === review.reviewId}
                  replyText={replyText}
                  replyByteLength={replyByteLength}
                  replyBytesRemaining={replyBytesRemaining}
                  submittingReply={submittingReply}
                  editingReplyReviewId={editingReplyReviewId}
                  onStartReply={handleStartReply}
                  onCancelReply={handleCancelReply}
                  onSubmitReply={handleSubmitReply}
                  onDeleteReply={handleDeleteReply}
                  onReplyTextChange={handleReplyTextChange}
                />
              ))}

              {/* Load More Button */}
              {nextPageToken && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    data-testid="google-reviews-load-more"
                    style={{
                      padding: '10px 24px',
                      background: loadingMore ? '#ccc' : '#021a4a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: loadingMore ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {loadingMore ? 'Lade...' : 'Mehr laden'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Help text for flagging inappropriate reviews */}
          <div
            style={{
              marginTop: 24,
              padding: 12,
              background: '#fff3cd',
              borderRadius: 6,
              fontSize: 13,
              color: '#856404',
            }}
          >
            Unangemessene Bewertungen können direkt bei Google gemeldet werden:{' '}
            <a
              href="https://support.google.com/business/answer/4596773"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0d47a1' }}
            >
              Bewertung melden
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoogleBusinessReviews;
